import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faServer, faShield, faCrown, faStar } from '@fortawesome/free-solid-svg-icons';
import ServerStatus from '../components/ServerStatus';
import DiscordStatus from '../components/DiscordStatus';

const HeroSection = styled.section`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4rem 8rem;
  position: relative;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);

  @media (max-width: 1200px) {
    padding: 4rem;
    text-align: center;
    min-height: auto;
    flex-direction: column;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    flex-direction: column;
    text-align: center;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 700px;
  z-index: 1;
  margin-right: 4rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 2rem;
    max-width: 100%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1;
  white-space: nowrap;
  width: fit-content;
  
  span {
    font-family: 'Caveat', cursive;
    font-size: 5rem;
    font-weight: 400;
  }
  
  background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    font-size: 3rem;
    white-space: normal;
    width: 100%;
    
    span {
      font-size: 3.5rem;
    }
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #aaa;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1.2rem 3rem;
  background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
  color: #fff;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(255, 51, 51, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(255, 51, 51, 0.5);
  }
`;

const StatsSection = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  text-align: center;
  background: rgba(255, 51, 51, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 51, 51, 0.2);
  transition: all 0.3s ease;
  min-width: 120px;

  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 51, 51, 0.15);
    border-color: var(--primary-color);
    box-shadow: 0 8px 20px rgba(255, 51, 51, 0.2);
  }
`;

const StatIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.3rem;
  font-family: 'Poppins', sans-serif;
`;

const StatLabel = styled.div`
  color: #aaa;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  
  img {
    width: 100%;
    max-width: 500px;
    height: auto;
    animation: float 6s ease-in-out infinite;
    
    @media (max-width: 768px) {
      max-width: 300px;
    }
    
    @media (min-width: 1400px) {
      max-width: 600px;
    }
  }
`;

const FeaturesSection = styled.section`
  padding: 3rem 8rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  
  @media (max-width: 1200px) {
    padding: 3rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: rgba(10, 10, 10, 0.8);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(255, 51, 51, 0.1), transparent 70%);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
    border-color: var(--primary-color);
    background: rgba(10, 10, 10, 0.9);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  background: rgba(255, 51, 51, 0.1);
  padding: 1.2rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    background: rgba(255, 51, 51, 0.15);
  }
`;

const FeatureTitle = styled.h3`
  color: #fff;
  margin-bottom: 1.2rem;
  font-size: 1.6rem;
`;

const FeatureDescription = styled.p`
  color: #aaa;
  line-height: 1.6;
  font-size: 1rem;
  max-width: 85%;
  margin: 0 auto;
`;

const StatusSection = styled.section`
  padding: 3rem 8rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  
  @media (max-width: 1200px) {
    padding: 3rem 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 3rem;
  margin-bottom: 4rem;
  color: #fff;
  position: relative;
  
  span {
    color: var(--primary-color);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  }
`;

const Home = ({ serverName, serverIP, discordInvite }) => {
  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            Welcome to <span>{serverName}</span>
          </HeroTitle>
          <HeroSubtitle>
            Join the most immersive Minecraft gaming experience with custom features, active community, and endless possibilities.
          </HeroSubtitle>
          <CTAButton href="#join">Start Playing Now</CTAButton>
          
          <StatsSection>
            <StatItem>
              <StatIcon>
                <FontAwesomeIcon icon={faUsers} />
              </StatIcon>
              <StatValue>1000+</StatValue>
              <StatLabel>Active Players</StatLabel>
            </StatItem>
            <StatItem>
              <StatIcon>
                <FontAwesomeIcon icon={faServer} />
              </StatIcon>
              <StatValue>99.9%</StatValue>
              <StatLabel>Uptime</StatLabel>
            </StatItem>
            <StatItem>
              <StatIcon>
                <FontAwesomeIcon icon={faStar} />
              </StatIcon>
              <StatValue>4.9/5</StatValue>
              <StatLabel>Rating</StatLabel>
            </StatItem>
          </StatsSection>
        </HeroContent>
        
        <HeroImage>
          <img src="/minecraft-character.png" alt="Minecraft Character" />
        </HeroImage>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>
          Our <span>Features</span>
        </SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>
              <FontAwesomeIcon icon={faServer} />
            </FeatureIcon>
            <FeatureTitle>High Performance</FeatureTitle>
            <FeatureDescription>
              Experience lag-free gameplay with our powerful dedicated servers.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>
              <FontAwesomeIcon icon={faUsers} />
            </FeatureIcon>
            <FeatureTitle>Active Community</FeatureTitle>
            <FeatureDescription>
              Join thousands of players in building amazing creations together.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>
              <FontAwesomeIcon icon={faShield} />
            </FeatureIcon>
            <FeatureTitle>Anti-Cheat</FeatureTitle>
            <FeatureDescription>
              Play fairly with our advanced anti-cheat system.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>
              <FontAwesomeIcon icon={faCrown} />
            </FeatureIcon>
            <FeatureTitle>Custom Features</FeatureTitle>
            <FeatureDescription>
              Enjoy unique gameplay elements and custom plugins.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <StatusSection>
        <SectionTitle>
          Community <span>Status</span>
        </SectionTitle>
        <StatusGrid>
          <ServerStatus serverIP={serverIP} />
          <DiscordStatus discordInvite={discordInvite} />
        </StatusGrid>
      </StatusSection>
    </>
  );
};

export default Home;
